// Trading, from the screens: one set of actions for a practice league and a
// real one (PLAN-2026-09-26-trades T6). The screen says what the manager
// did; this sends it to the simulator or to the API and answers the same
// way either way — done, with the trade as it now stands, or refused, with
// the sentence to show. The rules are the engine's in both.
//
// THE OFFER IS KEYED (B-150's rule, `moves.ts`): the same offer sent again
// after no answer carries the same `Idempotency-Key` and is the first offer,
// not a second one in the other manager's list. An answer to a trade needs no
// key — a second accept is refused as no longer open, which is the truth.

import { useMemo, useRef } from 'react';
import {
  TradeView as TradeViewSchema,
  TradesView as TradesViewSchema,
  type TradeActionRequest,
  type TradePathAction,
  type TradeProposalRequest,
  type TradeView,
  type TradesView,
} from '@fsp/domain';
import type { TradeEvent, TradeIntent } from '@fsp/trades';
import { messageOf, send, type Reply } from './api';
import { useLeagueSource } from './leagueSource';
import { AttemptKeys, thenRefresh } from './moves';
import { useSim } from './SimProvider';

export type TradeOutcome =
  | { ok: true; trade: TradeView | null; event: TradeEvent['type'] | null }
  | { ok: false; message: string };

export interface TradeActions {
  propose(body: TradeProposalRequest): Promise<TradeOutcome>;
  answer(tradeId: string, action: TradePathAction, body: TradeActionRequest): Promise<TradeOutcome>;
  /** My block as it should stand. */
  setBlock(entries: { playerId: string; wants: string }[]): Promise<TradeOutcome>;
}

const UNREACHABLE =
  'Could not reach the site, so we cannot tell whether that went through. Look at the list, then try again.';

function fromReply(reply: Reply<TradeView | TradesView>): TradeOutcome {
  if (reply.status === 'ok')
    return {
      ok: true,
      trade: 'sides' in reply.data ? reply.data : null,
      event: null,
    };
  return { ok: false, message: messageOf(reply, 'That did not go through.', UNREACHABLE) };
}

function fromEvents(events: TradeEvent[]): TradeOutcome {
  const rejected = events.find((e) => e.type === 'rejected');
  if (rejected && rejected.type === 'rejected') return { ok: false, message: rejected.message };
  return { ok: true, trade: null, event: events[0]?.type ?? null };
}

/**
 * The actions for the league in the route. `myTeamId` is who the simulator's
 * intents are by; a real league knows from the session.
 */
export function useTradeActions(
  leagueId: string | null,
  myTeamId: string | null,
  block: readonly { playerId: string; wants: string }[],
): TradeActions {
  const sim = useSim();
  const real = useLeagueSource() === 'api';
  const keys = useRef(new AttemptKeys());
  return useMemo<TradeActions>(() => {
    if (real && leagueId !== null) {
      return {
        async propose(body) {
          const key = keys.current.keyFor(body);
          const reply = await thenRefresh(
            leagueId,
            send('POST', `/leagues/${leagueId}/trades`, body, TradeViewSchema, {
              headers: { 'idempotency-key': key },
            }),
          );
          keys.current.settled(reply);
          return fromReply(reply);
        },
        async answer(tradeId, action, body) {
          return fromReply(
            await thenRefresh(
              leagueId,
              send(
                'POST',
                `/leagues/${leagueId}/trades/${encodeURIComponent(tradeId)}/${action}`,
                body,
                TradeViewSchema,
              ),
            ),
          );
        },
        async setBlock(entries) {
          if (myTeamId === null) return { ok: false, message: 'You have no team in this league.' };
          return fromReply(
            await thenRefresh(
              leagueId,
              send(
                'PUT',
                `/leagues/${leagueId}/teams/${encodeURIComponent(myTeamId)}/trade-block`,
                { entries },
                TradesViewSchema,
              ),
            ),
          );
        },
      };
    }
    const by = myTeamId ?? '';
    return {
      async propose(body) {
        return fromEvents(sim.tradeIntent({ type: 'propose', by, ...body }));
      },
      async answer(tradeId, action, body) {
        const base = { tradeId, by };
        const intent: TradeIntent =
          action === 'accept'
            ? { type: 'accept', ...base, drops: body.drops }
            : action === 'decline'
              ? { type: 'decline', ...base, reason: body.reason }
              : action === 'withdraw'
                ? { type: 'withdraw', ...base }
                : action === 'vote'
                  ? { type: 'vote', ...base, veto: body.veto }
                  : action === 'approve'
                    ? { type: 'approve', ...base }
                    : { type: 'veto', ...base, reason: body.reason ?? '' };
        return fromEvents(sim.tradeIntent(intent));
      },
      async setBlock(entries) {
        // The simulator keeps its block a player at a time: take off who is
        // gone, then put on or reword who is listed.
        for (const was of block)
          if (!entries.some((e) => e.playerId === was.playerId)) {
            const err = sim.setTradeBlock(was.playerId, null);
            if (err) return { ok: false, message: err };
          }
        for (const e of entries) {
          const same = block.find((b) => b.playerId === e.playerId && b.wants === e.wants);
          if (same) continue;
          const err = sim.setTradeBlock(e.playerId, e.wants);
          if (err) return { ok: false, message: err };
        }
        return { ok: true, trade: null, event: null };
      },
    };
  }, [real, leagueId, myTeamId, sim, block]);
}
